import { useEffect, useState } from 'react';
import { Cookie, Settings2, X } from 'lucide-react';
import { useConsent } from '@/contexts/ConsentContext';

type OptionalPreferences = {
  analytics: boolean;
  externalMedia: boolean;
  advertising: boolean;
};

const preferenceRows: Array<{
  key: keyof OptionalPreferences;
  label: string;
  description: string;
}> = [
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Helps us understand site usage and improve Sunny 106.5 through Google Analytics.',
  },
  {
    key: 'externalMedia',
    label: 'External media',
    description: 'Allows embedded content from services such as Facebook. These providers may collect usage data.',
  },
  {
    key: 'advertising',
    label: 'Advertising',
    description: 'Allows technologies used to measure advertising or provide more relevant promotions.',
  },
];

export function CookieConsent() {
  const {
    preferences,
    hasDecision,
    isPreferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useConsent();
  const [draft, setDraft] = useState<OptionalPreferences>({
    analytics: false,
    externalMedia: false,
    advertising: false,
  });

  useEffect(() => {
    if (isPreferencesOpen) {
      setDraft({
        analytics: preferences.analytics,
        externalMedia: preferences.externalMedia,
        advertising: preferences.advertising,
      });
    }
  }, [isPreferencesOpen, preferences]);

  return (
    <>
      {!hasDecision && !isPreferencesOpen && (
        <section
          className="fixed inset-x-0 bottom-0 z-[80] border-t border-slate-300 bg-white shadow-[0_-12px_35px_rgba(15,23,42,0.18)]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <div className="container mx-auto flex max-w-6xl flex-col gap-5 px-4 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-2 flex items-center gap-2">
                <Cookie className="h-5 w-5 text-primary" aria-hidden="true" />
                <h2 id="cookie-banner-title" className="text-lg font-bold text-slate-950">Your privacy choices</h2>
              </div>
              <p id="cookie-banner-description" className="text-sm leading-6 text-slate-700">
                We use essential technologies to operate this site. With your permission, we also use analytics
                and external media technologies to improve the site and display third-party content. You can
                change your choice at any time. <a href="/privacy" className="font-semibold text-primary underline underline-offset-2">Privacy Policy</a>
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
              <button type="button" onClick={rejectNonEssential} className="btn-secondary min-h-11">
                Reject Non-Essential
              </button>
              <button type="button" onClick={openPreferences} className="min-h-11 rounded-md border border-slate-400 bg-white px-4 py-2 font-semibold text-slate-900 hover:bg-slate-100">
                Manage Preferences
              </button>
              <button type="button" onClick={acceptAll} className="btn-primary min-h-11">
                Accept All
              </button>
            </div>
          </div>
        </section>
      )}

      {isPreferencesOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/60 p-4" role="presentation">
          <section
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-preferences-title"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" aria-hidden="true" />
                <h2 id="cookie-preferences-title" className="text-xl font-bold text-slate-950">Cookie Preferences</h2>
              </div>
              {hasDecision && (
                <button type="button" onClick={closePreferences} className="rounded-md p-2 text-slate-600 hover:bg-slate-100" aria-label="Close cookie preferences">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="space-y-3 p-5">
              <p className="text-sm leading-6 text-slate-700">
                Optional technologies remain off unless you enable them. Your choice is stored in this browser
                and can be changed using the Cookie Preferences link in the footer.
              </p>
              <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4">
                <div>
                  <h3 className="font-bold text-slate-950">Necessary</h3>
                  <p className="mt-1 text-sm leading-5 text-slate-600">Required for security, account access, playback requests, and remembering your privacy choice.</p>
                </div>
                <span className="shrink-0 rounded bg-slate-200 px-2 py-1 text-xs font-bold text-slate-700">Always active</span>
              </div>
              {preferenceRows.map((row) => (
                <label key={row.key} className="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <span>
                    <span className="block font-bold text-slate-950">{row.label}</span>
                    <span className="mt-1 block text-sm leading-5 text-slate-600">{row.description}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={draft[row.key]}
                    onChange={(event) => setDraft((current) => ({ ...current, [row.key]: event.target.checked }))}
                    className="mt-1 h-5 w-5 shrink-0 accent-primary"
                  />
                </label>
              ))}
            </div>

            <div className="flex flex-col gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
              <button type="button" onClick={rejectNonEssential} className="btn-secondary min-h-11">Reject Non-Essential</button>
              <button type="button" onClick={() => savePreferences(draft)} className="btn-primary min-h-11">Save Preferences</button>
              <button type="button" onClick={acceptAll} className="min-h-11 rounded-md border border-primary px-4 py-2 font-semibold text-primary hover:bg-primary/10">Accept All</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

